//@ts-nocheck
import { Select } from 'antd';
import type { SelectProps } from 'antd';
import * as React from 'react';
import styled from 'styled-components';

interface FormSelectProps extends SelectProps {
  width?: string;
  radius?: string;
  bordercolor?: string;
  height?: string;
  marginright?: string;
  textalign?: string;
  bgcolor?: string;
  selected_bg_color?: string;
  isFilter?: boolean;
}

export const SCOPE_FORM_SELECT_CLASS = 'scope-form-select';

const formSelectCustomProps = new Set([
  'isFilter',
  'width',
  'radius',
  'bordercolor',
  'height',
  'marginright',
  'textalign',
  'bgcolor',
  'selected_bg_color',
]);

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T | null) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<T | null>).current = node;
      }
    });
  };
}

function getSelectRootNode(
  holder: { nativeElement?: HTMLElement } | HTMLElement | null,
) {
  if (!holder) {
    return null;
  }

  const native =
    'nativeElement' in holder && holder.nativeElement
      ? holder.nativeElement
      : (holder as HTMLElement);

  if (native?.classList?.contains('ant-select')) {
    return native;
  }

  return native?.querySelector?.('.ant-select') ?? null;
}

function syncFormSelectA11y(
  holder: { nativeElement?: HTMLElement } | HTMLElement | null,
  ariaLabel?: string,
  ariaLabelledBy?: string,
) {
  const root = getSelectRootNode(holder);
  if (!root) {
    return;
  }

  // Form.Item / rc-select can mirror aria-* on the root div where they are invalid.
  root.removeAttribute('aria-required');
  root.removeAttribute('role');
  root.removeAttribute('aria-label');

  const labelledElement =
    root.querySelector<HTMLElement>('.ant-select-selection-search-input') ??
    root.querySelector<HTMLElement>('input[role="combobox"]') ??
    root.querySelector<HTMLElement>('[role="combobox"]');

  if (!labelledElement) {
    return;
  }

  if (ariaLabelledBy) {
    labelledElement.setAttribute('aria-labelledby', ariaLabelledBy);
    labelledElement.removeAttribute('aria-label');
    return;
  }

  if (ariaLabel) {
    labelledElement.setAttribute('aria-label', ariaLabel);
    // Do not set the search input's native placeholder — Ant Select already
    // renders `.ant-select-selection-placeholder`. Duplicating the string on
    // the input causes overlapping "double" placeholder text (esp. showSearch).
    if (labelledElement instanceof HTMLInputElement) {
      labelledElement.removeAttribute('placeholder');
    }
  }
}

const StyledFormSelect = styled(Select)
  .withConfig({
    shouldForwardProp: (prop) => !formSelectCustomProps.has(prop),
  })
  .attrs((props) => ({
    rootClassName: [SCOPE_FORM_SELECT_CLASS, props.rootClassName]
      .filter(Boolean)
      .join(' '),
  }))<FormSelectProps>`
  font-family: var(--font-family, Inter, sans-serif) !important;

  width: ${(props) => (props.width ? props.width : '90%')} !important;
  border-radius: ${(props) => (props.radius ? props.radius : '0')} !important;
  border: 1px solid
    ${(props) => (props.bordercolor ? props.bordercolor : 'transparent')};
  min-height: ${(props) => (props.height ? props.height : '45px')};
  margin-right: ${(props) => (props.marginright ? props.marginright : '0')};
  text-align: ${(props) => (props.textalign ? props.textalign : 'none')};
  min-width: 25%;
  &:hover,
  &:focus,
  &:active {
    border: 1px solid var(--color-primary, #41aaaf) !important;
    box-shadow: none;
    min-height: ${(props) => (props.height ? props.height : '43px')};
  }

  &.ant-select:not(.ant-select-customize-input) .ant-select-selector {
    border-radius: ${(props) => (props.radius ? props.radius : '0')} !important;
    border: 1px solid transparent !important;
  }

  &:not(.ant-select-disabled):hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector,
  &.ant-select-open .ant-select-selector {
    border: 1px solid transparent !important;
    box-shadow: none !important;
  }

  .ant-select-arrow {
    color: var(--color-primary, #41aaaf) !important;
  }

  .ant-select-selector {
    min-height: ${(props) =>
      props.height && props.mode == 'multiple' ? props.height : '43px'};
    background: ${(props) =>
      props.bgcolor
        ? props.bgcolor
        : 'var(--color-surface-raised, #FFFFFF)'} !important;
    display: flex;
    align-items: center;
  }

  .ant-select-selector {
    min-height: ${(props) =>
      props.height && props.isFilter
        ? `${props.height.replace('px', '') - 5}px`
        : '43px'};
  }

  .ant-select-selection-placeholder {
    color: var(--color-placeholder, #9ca7b0) !important;
    font-size: var(--font-size-base, 14px) !important;
    font-weight: 400 !important;
    font-family: inherit !important;
  }

  .ant-select-selection-item {
    color: var(--color-text-base, var(--color-text-dark, #042a2a));
    font-weight: 600;
    font-family: inherit !important;
  }

  .ant-select-selection-search-input {
    font-family: inherit !important;
    color: var(--color-text-base, var(--color-text-dark, #042a2a)) !important;
  }

  ${(props) =>
    props.disabled &&
    `.ant-select-selection-item-content {
      color: rgba(46, 46, 46, 0.75) !important;
  }`}

  ${(props) =>
    props.mode == 'multiple' &&
    `
  &.ant-select-multiple .ant-select-selector {
    display: flex;
    align-items: center;
    padding-inline: 11px;
  }

  &.ant-select-multiple .ant-select-selection-wrap {
    align-self: stretch;
    display: flex;
    align-items: center;
  }

  &.ant-select-multiple .ant-select-selection-placeholder {
    inset-inline-start: 11px !important;
    inset-inline-end: 22px;
    top: 50% !important;
    transform: translateY(-50%) !important;
    margin: 0 !important;
    line-height: 1.25 !important;
  }

  .ant-select-selection-item {
    background: ${
      props.selected_bg_color ? props.selected_bg_color : '#fff5e2'
    } !important;
    border-radius: 5px;
    height:35px;
    line-height: 32px;
    font-size: 14px;
    font-weight: 600;
  }
  
  .ant-select-selection-item > span > div > img {
    margin-top: 8px !important;
  }
  `}

  .ant-select-selection-item > div > img {
    margin-top: 7px !important;
  }

  .ant-select-selection-item-remove {
    color: var(--color-primary, #41aaaf) !important;
    font-weight: 800;
    font-size: 12px;
  }
`;

export const FormSelect = React.forwardRef<any, FormSelectProps>(
  function FormSelect(props, ref) {
    const {
      'aria-required': _ariaRequired,
      role: _role,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      id,
      ...rest
    } = props;

    const selectRef = React.useRef(null);

    React.useLayoutEffect(() => {
      syncFormSelectA11y(selectRef.current, ariaLabel, ariaLabelledBy);
    }, [ariaLabel, ariaLabelledBy, id]);

    return (
      <StyledFormSelect
        ref={mergeRefs(ref, selectRef)}
        id={id}
        {...rest}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      />
    );
  },
);

// forwardRef wrappers do not inherit antd Select statics (Option / OptGroup).
Object.assign(FormSelect, {
  Option: Select.Option,
  OptGroup: Select.OptGroup,
});

interface FilterSelectProps extends SelectProps {
  mode?: string;
  placeholder?: string;
  width?: string;
  height?: string;
}

export const SCOPE_FILTER_SELECT_CLASS = 'scope-filter-select';

const filterSelectCustomProps = new Set(['width', 'height']);

const StyledFilterSelect = styled(Select)
  .withConfig({
    shouldForwardProp: (prop) => !filterSelectCustomProps.has(prop),
  })
  .attrs((props) => ({
    rootClassName: [SCOPE_FILTER_SELECT_CLASS, props.rootClassName]
      .filter(Boolean)
      .join(' '),
    title:
      props.title ??
      (typeof props.placeholder === 'string' ? props.placeholder : undefined),
  }))<FilterSelectProps>`
  width: ${(props) =>
    props.width && props.width !== '100%' ? props.width : 'auto'};
  min-width: 130px;
  height: 32px;
  gap: 0px;
  border-radius: 10px !important;
  border: 1px solid var(--color-border, #dbeae3);
  opacity: 0px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.05);

  &.ant-select:not(.ant-select-customize-input) .ant-select-selector {
    border: 1px solid #cccccc !important;
    border-radius: 10px !important;
    box-shadow: none !important;
  }

  &.selected-filter .ant-select-selector {
    border: 2px solid var(--color-primary, #41aaaf) !important;
  }

  &.selected-filter .ant-select-selector::after {
    content: '${(props) =>
      props.mode == 'multiple' && props.placeholder
        ? props.placeholder
        : ''}' !important;
    visibility: visible !important;
    color: var(--color-placeholder, #9ca7b0);
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
    width: ${(props) =>
      props.mode == 'multiple' && props.placeholder ? 'auto' : '0px'};
    max-width: calc(100% - 28px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    pointer-events: none;
  }

  .ant-select-selection-placeholder {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > div > span {
    font-size: 12px;
    font-weight: 600;
    line-height: 12px;
    text-align: left;
  }

  &:not(.ant-select-disabled):hover .ant-select-selector {
    border: 2px solid var(--color-primary, #41aaaf) !important;
  }

  .ant-select-arrow {
    color: var(--color-text-base, #2e2e2e) !important;
  }

  ${(props) =>
    props.mode == 'multiple' &&
    `.ant-select-selection-overflow {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }

    .ant-select-selection-item {
      display: none !important;
    }`}

  .ant-select-clear {
    cursor: pointer;
    opacity: 0.45;
    transition: opacity 0.2s;
    pointer-events: auto;
    color: var(--color-text-base, #2e2e2e) !important;

    .anticon,
    svg {
      color: currentColor !important;
    }

    svg path[stroke] {
      stroke: currentColor !important;
    }

    svg path[fill]:not([fill='none']) {
      fill: currentColor !important;
    }

    &:hover {
      opacity: 1;
    }
  }

  html[data-theme='dark'] & .ant-select-clear,
  html[data-theme='dark'] & .ant-select-clear .anticon,
  html[data-theme='dark'] & .ant-select-clear svg {
    color: var(--color-text-muted, #a9b8b9) !important;
  }

  html[data-theme='dark'] & .ant-select-clear svg,
  html[data-theme='dark'] & .ant-select-clear .anticon svg {
    fill: var(--color-text-muted, #a9b8b9) !important;
  }

  html[data-theme='dark'] & .ant-select-clear svg path {
    fill: var(--color-text-muted, #a9b8b9) !important;
  }
`;

export const FilterSelect = React.forwardRef<any, FilterSelectProps>(
  function FilterSelect(props, ref) {
    const {
      'aria-required': _ariaRequired,
      role: _role,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      placeholder,
      open,
      value,
      ...rest
    } = props;

    const accessibleName =
      ariaLabel ??
      (typeof placeholder === 'string' && placeholder.length > 0
        ? placeholder
        : undefined);

    const selectRef = React.useRef(null);

    React.useLayoutEffect(() => {
      syncFormSelectA11y(selectRef.current, accessibleName, ariaLabelledBy);
    }, [accessibleName, ariaLabelledBy, open, value]);

    return (
      <StyledFilterSelect
        ref={mergeRefs(ref, selectRef)}
        {...rest}
        open={open}
        value={value}
        placeholder={placeholder}
        aria-label={accessibleName}
        aria-labelledby={ariaLabelledBy}
      />
    );
  },
);

Object.assign(FilterSelect, {
  Option: Select.Option,
  OptGroup: Select.OptGroup,
});
